/**
 * Dialog "Lengkapi Identitas Invoice" — melengkapi identitas tanpa mengubah
 * nominal. Dibatasi perm `collection.complete_identity`.
 *
 * Expanded 2026-10-21 (user request C):
 *  - Jenis transaksi sumber (readonly auto-detect income_entry / retention)
 *  - ID transaksi sumber (editable utk klaim retensi) + preview label +
 *    tombol test "Buka Transaksi Sumber"
 *  - PIC/penanggung jawab (pilih dari daftar user)
 *  - Status verifikasi + alasan (bila belum terverifikasi)
 *  - Readonly: proyek/WO/client, updated_at + updated_by
 *  - Nominal/pembayaran/saldo DILARANG diubah dari form ini
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info, ExternalLink, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fmtDate } from "@/lib/constants";
import api from "@/lib/api";

const Field = ({ label, children, hint, required, readOnly }) => (
  <label className="block space-y-1">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1">
      {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
      {readOnly && <Lock size={9} className="text-slate-400" aria-label="Hanya baca" />}
    </div>
    {children}
    {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
  </label>
);

const inputCls = "w-full border border-slate-300 px-2 h-8 text-xs bg-white focus:outline-none focus:border-[#FF5A1F]";
const roCls = "w-full border border-slate-200 bg-slate-50 px-2 h-8 text-xs text-slate-700 flex items-center";

export default function CompleteIdentityDialog({ open, row, onClose, onSaved }) {
  const nav = useNavigate();
  const ident = row?.identity;
  const [form, setForm] = useState({
    invoice_number: "", external_reference: "", invoice_date: "", due_date: "",
    retention_source_project_id: "", retention_source_entry_id: "",
    pic_id: "", verification_reason: "",
  });
  const [confirmEarlier, setConfirmEarlier] = useState(false);
  const [reason, setReason] = useState("");
  const [dupe, setDupe] = useState({ loading: false, matches: [] });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const isRetention = ident?.txn_kind === "klaim_retensi";
  const sourceKind = ident?.source?.kind || (isRetention ? "retention_source_invoice" : "income_entry");

  useEffect(() => {
    if (!open || !ident) return;
    setForm({
      invoice_number: ident.invoice_number || "",
      external_reference: ident.reference_source === "manual" ? (ident.external_reference || "") : "",
      invoice_date: ident.invoice_date || "",
      due_date: ident.due_date || "",
      retention_source_project_id: isRetention ? (ident.source?.project_id || "") : "",
      retention_source_entry_id: isRetention ? (ident.source?.entry_id || "") : "",
      pic_id: ident.pic_id || "",
      verification_reason: ident.verification_status?.reason || "",
    });
    setConfirmEarlier(false);
    setReason("");
    setDupe({ loading: false, matches: [] });
  }, [open, ident, isRetention]);

  useEffect(() => {
    if (!open) return;
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [open]);

  useEffect(() => {
    const inv = (form.invoice_number || "").trim();
    if (!inv || inv === (ident?.invoice_number || "")) { setDupe({ loading: false, matches: [] }); return; }
    let cancelled = false;
    setDupe({ loading: true, matches: [] });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/collection/invoices/duplicate-check", {
          params: { invoice_number: inv, exclude_entry_id: ident?.entry_id || "" },
        });
        if (!cancelled) setDupe({ loading: false, matches: data.matches || [] });
      } catch { if (!cancelled) setDupe({ loading: false, matches: [] }); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.invoice_number, ident?.invoice_number, ident?.entry_id]);

  const dueEarlier = useMemo(() =>
    !!(form.invoice_date && form.due_date && form.due_date < form.invoice_date),
    [form.invoice_date, form.due_date]);

  const whitespaceInvoice = form.invoice_number.length > 0 && !form.invoice_number.trim();
  const needsVerificationReason = !ident?.verification_status?.verified;

  // Preview tautan sumber yang DIBANGKITKAN DARI FORM (bukan simpan belum
  // dilakukan) — supaya tombol "Buka Transaksi Sumber" dapat diuji dulu.
  const previewLink = useMemo(() => {
    if (isRetention) {
      const pid = form.retention_source_project_id, eid = form.retention_source_entry_id;
      return pid && eid ? `/collection?focus=${pid}::${eid}` : null;
    }
    return ident?.source?.link || null;
  }, [isRetention, form.retention_source_project_id, form.retention_source_entry_id, ident?.source?.link]);

  const save = async () => {
    if (whitespaceInvoice) { toast.error("Nomor invoice tidak boleh hanya spasi."); return; }
    if (dueEarlier && (!confirmEarlier || !reason.trim())) {
      toast.error("Konfirmasi + alasan wajib saat jatuh tempo lebih awal dari tanggal invoice.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        invoice_number: form.invoice_number.trim() || null,
        external_reference: form.external_reference.trim() || null,
        invoice_date: form.invoice_date || null,
        due_date: form.due_date || null,
        pic_id: form.pic_id || null,
        verification_reason: form.verification_reason.trim() || null,
        confirm_due_earlier: dueEarlier && confirmEarlier,
        reason: reason.trim() || null,
      };
      if (isRetention) {
        payload.retention_source_project_id = form.retention_source_project_id || null;
        payload.retention_source_entry_id = form.retention_source_entry_id || null;
      }
      const { data } = await api.patch(
        `/collection/invoices/${encodeURIComponent(row.project_id)}/${encodeURIComponent(row.entry_id)}/identity`,
        payload,
      );
      toast.success("Identitas invoice diperbarui.");
      if (data.duplicate_warning?.length) {
        toast.warning(`Peringatan: nomor invoice sama digunakan oleh ${data.duplicate_warning.length} transaksi lain.`);
      }
      onSaved?.(data.row);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan.");
    } finally { setSaving(false); }
  };

  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="complete-identity-dialog" aria-describedby="complete-identity-desc">
        <DialogHeader>
          <DialogTitle className="text-[#0B1D3A]">Lengkapi Identitas Invoice</DialogTitle>
          <p id="complete-identity-desc" className="text-[11px] text-slate-500">
            Memperbarui identitas invoice. Nominal, pembayaran, dan saldo piutang TIDAK dapat diubah dari form ini.
          </p>
        </DialogHeader>

        {/* Read-only context */}
        <div className="grid grid-cols-2 gap-2 mt-2 text-[11px] bg-slate-50 border border-slate-200 p-2" data-testid="identity-readonly-context">
          <div><span className="text-slate-500">Proyek / WO</span><div className="font-semibold text-[#0B1D3A]" data-testid="ro-project">{ident?.project_name || "—"}</div></div>
          <div><span className="text-slate-500">Client</span><div className="font-semibold text-[#0B1D3A]" data-testid="ro-client">{ident?.client || "—"}</div></div>
          <div><span className="text-slate-500">Terakhir Diubah</span><div className="text-slate-700" data-testid="ro-updated">{ident?.last_updated_at ? fmtDate(ident.last_updated_at) : "—"}</div></div>
          <div><span className="text-slate-500">Oleh</span><div className="text-slate-700" data-testid="ro-updated-by">{row.identity_edited_by_name || row.pic_collection_name || "—"}</div></div>
        </div>

        <div className="space-y-3 mt-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nomor Invoice" hint="Tidak boleh hanya spasi; peringatan jika duplikat dengan transaksi lain.">
              <input data-testid="input-invoice-number" className={inputCls}
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
            </Field>
            <Field label="Referensi Eksternal" hint="Nomor PO / dokumen client. Fallback transaksi sumber tidak disalin otomatis.">
              <input data-testid="input-external-ref" className={inputCls}
                value={form.external_reference}
                onChange={(e) => setForm({ ...form, external_reference: e.target.value })} />
            </Field>
          </div>
          {dupe.loading && <div className="text-[10px] text-slate-500 italic">Memeriksa duplikat…</div>}
          {!dupe.loading && dupe.matches.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 p-2 text-[10px] text-amber-900" data-testid="duplicate-warning">
              <div className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle size={11} /> Nomor invoice sudah dipakai {dupe.matches.length} transaksi lain</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {dupe.matches.slice(0, 5).map((m) => (
                  <li key={m.entry_id}>{m.project_name} · {m.client || "—"} · {m.invoice_date || "tanpa tanggal"}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tanggal Invoice">
              <input type="date" data-testid="input-invoice-date" className={inputCls}
                value={form.invoice_date || ""}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
            </Field>
            <Field label="Tanggal Jatuh Tempo">
              <input type="date" data-testid="input-due-date" className={inputCls}
                value={form.due_date || ""}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </Field>
          </div>
          {dueEarlier && (
            <div className="border border-rose-300 bg-rose-50 p-2 space-y-2 text-[11px]" data-testid="due-earlier-warning">
              <div className="flex items-center gap-1 text-rose-800 font-semibold"><AlertTriangle size={12} /> Jatuh tempo lebih awal dari tanggal invoice</div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" data-testid="input-confirm-earlier"
                  checked={confirmEarlier} onChange={(e) => setConfirmEarlier(e.target.checked)} className="mt-0.5" />
                <span>Saya mengonfirmasi urutan tanggal ini benar untuk invoice tersebut.</span>
              </label>
              <textarea data-testid="input-reason" rows={2} placeholder="Alasan (wajib)"
                className="w-full border border-slate-300 p-1.5 text-xs bg-white"
                value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}

          {/* Transaksi Sumber (user request C.1-C.3) */}
          <div className="border border-slate-200 p-2 space-y-2 bg-white" data-testid="source-section">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Transaksi Sumber</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Jenis Transaksi Sumber" readOnly>
                <div className={roCls} data-testid="source-kind">
                  {sourceKind === "retention_source_invoice" ? "Invoice sumber retensi (klaim retensi)" : "Entri pendapatan proyek"}
                </div>
              </Field>
              {isRetention ? (
                <>
                  <Field label="Project ID Sumber" hint="UUID proyek yang menerbitkan invoice retensi sumber.">
                    <input data-testid="input-source-project-id" className={inputCls}
                      value={form.retention_source_project_id}
                      onChange={(e) => setForm({ ...form, retention_source_project_id: e.target.value })} />
                  </Field>
                  <Field label="Entry ID Sumber" hint="UUID entry invoice retensi sumber.">
                    <input data-testid="input-source-entry-id" className={inputCls}
                      value={form.retention_source_entry_id}
                      onChange={(e) => setForm({ ...form, retention_source_entry_id: e.target.value })} />
                  </Field>
                </>
              ) : (
                <Field label="ID Sumber" readOnly hint="Entri pendapatan proyek — otomatis dari sistem.">
                  <div className={`${roCls} font-mono`} data-testid="source-id-readonly">{ident?.source?.entry_id || "—"}</div>
                </Field>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[10px] text-slate-500">Preview:</span>
              {previewLink ? (
                <button type="button" data-testid="btn-test-source-link"
                  onClick={() => nav(previewLink)}
                  className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900 underline">
                  <ExternalLink size={11} /> Buka Transaksi Sumber
                </button>
              ) : (
                <span className="italic text-[11px] text-slate-400" data-testid="source-link-not-available">
                  Tautan sumber belum tersedia (ID belum lengkap).
                </span>
              )}
            </div>
          </div>

          {/* PIC & Status Verifikasi (user request C.4-C.5) */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Penanggung Jawab (PIC)">
              <select data-testid="input-pic" className={inputCls}
                value={form.pic_id}
                onChange={(e) => setForm({ ...form, pic_id: e.target.value })}>
                <option value="">— Belum ditetapkan —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Status Verifikasi" readOnly hint="Diubah lewat alur verifikasi, bukan form ini.">
              <div className={roCls} data-testid="verification-status-ro">
                {ident?.verification_status?.label || "—"}
              </div>
            </Field>
          </div>
          {needsVerificationReason && (
            <Field label="Alasan Perlu Verifikasi" hint="Isi penjelasan supaya penanggung jawab tahu apa yang harus dilengkapi.">
              <textarea data-testid="input-verification-reason" rows={2}
                className="w-full border border-slate-300 p-1.5 text-xs bg-white"
                value={form.verification_reason}
                onChange={(e) => setForm({ ...form, verification_reason: e.target.value })} />
            </Field>
          )}

          <div className="text-[10px] text-slate-500 flex items-start gap-1" data-testid="immutable-nominal-notice">
            <Info size={11} className="mt-0.5 shrink-0" />
            Nilai bruto, PPh/potongan, pembayaran, alokasi, outstanding, retensi, dan status piutang berdasarkan nilai — TIDAK diubah lewat form ini. Hanya identitas (nomor, referensi, tanggal, PIC, sumber, alasan verifikasi).
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
          <Button type="button" data-testid="btn-save-identity"
            disabled={saving || whitespaceInvoice}
            onClick={save}
            className="bg-[#0B1D3A] hover:bg-[#1a2e52]">
            {saving ? "Menyimpan…" : "Simpan Identitas"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
